import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { Component } from "@theme/component";

/**
 * @typedef {Object} ProductSavingsRefs
 * @property {HTMLElement} savingsContainer
 */

/**
 * A custom element that displays product price savings.
 * This component listens for variant update events and updates the savings display accordingly.
 *
 * @extends {Component<ProductSavingsRefs>}
 */
class ProductSavings extends Component {
  requiredRefs = ["savingsContainer"];

  /**
   * Updates the savings display.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSavings = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const { savingsContainer } = this.refs;

    const newProductSavings = event.detail.data.html.querySelector(
      `product-savings[data-block-id="${this.dataset.blockId}"]`,
    );

    if (!newProductSavings) return;

    const newSavingsContainer = newProductSavings.querySelector(
      '[ref="savingsContainer"]',
    );

    if (newSavingsContainer && savingsContainer) {
      savingsContainer.replaceWith(newSavingsContainer);
    }

    // Sync display from the server-rendered markup (hide/show based on savings)
    if (newProductSavings instanceof HTMLElement) {
      this.style.display = newProductSavings.style.display;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.addEventListener(
      ThemeEvents.variantUpdate,
      this.updateSavings,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updateSavings,
    );
  }
}

if (!customElements.get("product-savings")) {
  customElements.define("product-savings", ProductSavings);
}
